import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import { resolveGroupApplicationIds } from './deliveryGroupModel';

export type StopExecutionOwner = {
  stopUid: string;
  executionOwnerGroupUid: string;
  type: 'loading' | 'unloading';
  position?: number;
};

/**
 * Maps every stop to its FE delivery group for execution ownership.
 * Does NOT use parentLoadingPosition or positional inference.
 */
export function buildExecutionOwnershipIndex(
  groups: DeliveryGroupDraft[]
): Map<string, StopExecutionOwner> {
  const index = new Map<string, StopExecutionOwner>();

  for (const group of groups) {
    for (const stop of group.loadingStops) {
      index.set(stop.uid, {
        stopUid: stop.uid,
        executionOwnerGroupUid: group.uid,
        type: 'loading',
        position: stop.position ?? undefined
      });
    }
    for (const stop of group.unloadingStops) {
      index.set(stop.uid, {
        stopUid: stop.uid,
        executionOwnerGroupUid: group.uid,
        type: 'unloading',
        position: stop.position ?? undefined
      });
    }

    if (group.isDefault && group.loadingStops[0]) {
      const primary = group.loadingStops[0];
      if (!index.has(primary.uid)) {
        index.set(primary.uid, {
          stopUid: primary.uid,
          executionOwnerGroupUid: group.uid,
          type: 'loading',
          position: 0
        });
      }
    }
  }

  return index;
}

export function resolveExecutionOwnerGroupUid(
  stopUid: string,
  groups: DeliveryGroupDraft[]
): string | undefined {
  return buildExecutionOwnershipIndex(groups).get(stopUid)?.executionOwnerGroupUid;
}

/** All loading stop UIDs owned by a delivery group (execution scope). */
export function loadingStopUidsForGroup(group: DeliveryGroupDraft): string[] {
  return group.loadingStops.map((s) => s.uid);
}

/** All unloading stop UIDs owned by a delivery group (execution scope). */
export function unloadingStopUidsForGroup(group: DeliveryGroupDraft): string[] {
  return group.unloadingStops.map((s) => s.uid);
}

/** Shared unload: same stopUid may receive allocations from multiple loading stops in same FE group. */
export function isSharedUnloadInGroup(
  unloadStopUid: string,
  group: DeliveryGroupDraft,
  fromStopDraft: Record<number, string | undefined>
): boolean {
  const loadingUids = new Set(loadingStopUidsForGroup(group));
  const sources = new Set<string>();
  for (const fromUid of Object.values(fromStopDraft)) {
    if (fromUid && loadingUids.has(fromUid)) sources.add(fromUid);
  }
  return (
    unloadingStopUidsForGroup(group).includes(unloadStopUid) && sources.size > 1
  );
}

export function resolveStopInGroups(
  stopUid: string,
  groups: DeliveryGroupDraft[]
): { group: DeliveryGroupDraft; stop: DeliveryGroupStopDraft } | undefined {
  for (const group of groups) {
    const stop =
      group.loadingStops.find((s) => s.uid === stopUid) ??
      group.unloadingStops.find((s) => s.uid === stopUid);
    if (stop) return { group, stop };
  }
  return undefined;
}

export function groupOwnsApplication(
  group: DeliveryGroupDraft,
  applicationId: number | undefined
): boolean {
  if (applicationId == null) return true;
  return resolveGroupApplicationIds(group).has(Number(applicationId));
}
